import React, { useState } from 'react';

import {
  View,
  Text,
  StyleSheet,
  Dimensions,
  Platform,
  TouchableOpacity,
} from 'react-native';
import { MaterialIcons, Ionicons } from '@expo/vector-icons';
import { ChargerAttributes } from '../models/charger-model';
import { ChargerHistoryAttributes } from '../models/charger-history-model';
import {
  formattedDate,
  formattedTimeNoSeconds,
} from '../components/common/utils/date-time-conversions';
import dayjs from 'dayjs';
import ChargerImageLoader from '../components/ChargerImageLoader';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import RoleService from '../services/role-service';

const { width, height } = Dimensions.get('window');

const ChargeHistoryCard = (props: {
  index: number;
  chargerHistory: ChargerHistoryAttributes;
  charger: ChargerAttributes;
  isPressable: boolean;
  showPaidOn?: boolean;
  viewContext: 'host' | 'driver'; // Explicitly define the perspective
}) => {
  const [showSplitBreakdown, setShowSplitBreakdown] = useState(false);
  const userSession = useSelector((state: RootState) => state.user?.session);
  const isHost = RoleService.isHost(userSession);

  // Determine the price to display based on VIEW CONTEXT, not user role
  // Host Context (MyChargers): Show gross revenue (kwhPrice) - what they earned before splits
  // Driver Context (TransactionHistory): Show total price (total_price) - what they paid including taxes/fees
  // This ensures correct display even if a host charges at their own charger
  const isHostView = props.viewContext === 'host';
  const totalPrice = isHostView
    ? (props.chargerHistory?.order?.kwhPrice ?? 0)
    : (props.chargerHistory?.order?.total_price ?? 0);
  
  // Calculate net revenue if partner splits exist (host view only)
  const partnerSplits = props.chargerHistory?.totalPartnerSplits ?? 0;
  const hasPartnerSplits = isHostView && partnerSplits > 0;
  const netRevenue = hasPartnerSplits ? totalPrice - partnerSplits : totalPrice;

  /**
   * Converts a number of seconds into hh:mm format.
   * @param seconds - The number of seconds to convert.
   * @returns A string in hh:mm format followed by "hours".
   */
  const convertSecondsToHHMM = (seconds: number | null): string => {
    if (seconds) {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);

      // Format minutes to be two digits, e.g., 01 instead of 1
      const formattedMinutes = minutes.toString().padStart(2, '0');

      return `${hours}:${formattedMinutes} hours`;
    } else {
      return `No charge`;
    }
  };

  const paidOn = props.chargerHistory?.payouts?.paid_on;

  // console.log(props.chargerHistory);
  // console.log(props.chargerHistory?.total_charge_price);

  return (
    <React.Fragment>
      <View
        className="mt-4 rounded-lg"
        key={`chargeHistoryCard${props.index}`}
        style={{ backgroundColor: '#292D33' }}
      >
        <View className="flex flex-row justify-between items-center p-3">
          <View className="flex flex-row items-center" style={{ flex: 1, gap: 12 }}>
            <View style={{ justifyContent: 'center', alignItems: 'center' }}>
              <ChargerImageLoader
                chargerId={props.charger?.uuid}
                style={{ width: 50, height: 50 }}
                fallbackSource={require('../../assets/chargerStation.png')}
                resizeMode="cover"
                alt="Charger station"
                circular={true}
              />
            </View>
            <View style={{ flex: 1 }}>
              <View style={styles.chargerNameRow}>
                <Text
                  className="text-base font-bold text-white"
                  style={[styles.alignEllipses]}
                >
                  {props.charger?.name}
                </Text>
                {props.chargerHistory?.hasSplits && (
                  <View style={styles.splitIndicator}>
                    <Ionicons name="people" size={12} color="#3B82F6" />
                  </View>
                )}
              </View>
              <Text
                style={[styles.textColor, styles.alignEllipses]}
                className="text-xs font-medium"
                numberOfLines={1} // Ensures a single line of text
                ellipsizeMode="tail" // Adds '...' if the text is too long
              >
                {props.charger?.full_address}
              </Text>
            </View>
          </View>
          {/* <View className='flex flex-row gap-1 items-center'> */}
          <View
            className="flex flex-col gap-1 items-end"
            style={{ marginRight: 10 }}
          >
            <Text className="text-xs font-bold text-white">
              {formattedDate(props.chargerHistory?.charging_start_time)}
            </Text>
            <Text
              style={[styles.textColor, { fontStyle: 'italic' }]}
              className="text-xs font-medium"
            >
              {formattedTimeNoSeconds(
                props.chargerHistory?.charging_start_time,
                props.chargerHistory?.tz ?? 'UTC',
                props.chargerHistory?.tz_offset
              )
                ?.split(' ')
                .slice(0, 2)
                .join(' ')}
            </Text>
            <Text
              style={[styles.textColor, { fontStyle: 'italic' }]}
              className="text-xs font-medium"
            >
              {props.chargerHistory?.tz ?? 'UTC'}
            </Text>
          </View>
        </View>
        <View className="flex flex-row flex-wrap justify-between items-center p-3">
          {[
            {
              label: 'Total time',
              value: convertSecondsToHHMM(
                props.chargerHistory?.total_charge_time
              ),
            },
            {
              label: 'Total kWh Charged',
              value: `${(props.chargerHistory?.total_kwh ?? 0).toFixed(2)} kWh`,
            },
            // Only show "Paid on" if showPaidOn prop is true
            ...(props.showPaidOn ? [{
              label: 'Paid on',
              value: paidOn ? dayjs(paidOn).format('MM/DD/YYYY') : '—',
            }] : []),
          ].map(({ label, value }) => (
            <View key={label} className="mb-3 w-1/2 md:w-1/4">
              <Text className="text-xs font-normal" style={styles.textColor1}>
                {label}
              </Text>
              <Text className="text-sm font-semibold" style={styles.textColor2}>
                {value}
              </Text>
            </View>
          ))}
          
          {/* Revenue Section with Partner Splits */}
          <View className="mb-3 w-1/2 md:w-1/4">
            {hasPartnerSplits ? (
              <TouchableOpacity 
                onPress={() => setShowSplitBreakdown(!showSplitBreakdown)}
                activeOpacity={0.7}
              >
                <View style={styles.revenueHeader}>
                  <Text className="text-xs font-normal" style={styles.textColor1}>
                    Gross Revenue
                  </Text>
                  <Ionicons 
                    name="information-circle" 
                    size={14} 
                    color="#3B82F6" 
                    style={{ marginLeft: 4 }}
                  />
                </View>
                <Text className="text-sm font-semibold" style={styles.textColor2}>
                  $ {totalPrice.toFixed(2)}
                </Text>
                <View style={styles.splitIndicatorBadge}>
                  <Ionicons name="remove-circle" size={10} color="#F59E0B" />
                  <Text style={styles.splitDeductionText}>
                    -${partnerSplits.toFixed(2)}
                  </Text>
                </View>
                <View style={styles.netRevenueRow}>
                  <Text style={styles.netRevenueLabel}>Net: </Text>
                  <Text style={styles.netRevenueValue}>
                    ${netRevenue.toFixed(2)}
                  </Text>
                </View>
              </TouchableOpacity>
            ) : (
              <>
                <Text className="text-xs font-normal" style={styles.textColor1}>
                  {isHostView ? 'Gross Revenue' : 'Total price'}
                </Text>
                <Text className="text-sm font-semibold" style={styles.textColor2}>
                  $ {totalPrice.toFixed(2)}
                </Text>
              </>
            )}
          </View>
          
          {props.isPressable && (
            <View>
              <MaterialIcons
                name="keyboard-arrow-right"
                size={24}
                color="white"
              />
            </View>
          )}
        </View>

        {/* Expanded Revenue Split Details */}
        {hasPartnerSplits && showSplitBreakdown && (
          <View style={styles.expandedSplitSection}>
            <View style={styles.splitDetailRow}>
              <Text style={styles.splitDetailLabel}>Gross Revenue:</Text>
              <Text style={styles.splitDetailValue}>${totalPrice.toFixed(2)}</Text>
            </View>
            <View style={styles.splitDetailRow}>
              <Text style={styles.splitDetailLabelDeduction}>Partner Splits:</Text>
              <Text style={styles.splitDetailValueDeduction}>-${partnerSplits.toFixed(2)}</Text>
            </View>
            <View style={styles.splitDividerThin} />
            <View style={[styles.splitDetailRow, { marginTop: 4 }]}>
              <Text style={styles.netRevenueExpandedLabel}>Your Net Revenue:</Text>
              <Text style={styles.netRevenueExpandedValue}>${netRevenue.toFixed(2)}</Text>
            </View>
          </View>
        )}

        {/* Revenue Split Breakdown Section */}
        {props.chargerHistory?.hasSplits && (
          <View style={styles.splitSection}>
            <TouchableOpacity
              style={styles.splitHeader}
              onPress={() => setShowSplitBreakdown(!showSplitBreakdown)}
            >
              <View style={styles.splitHeaderLeft}>
                <Ionicons name="people" size={16} color="#3B82F6" />
                <Text style={styles.splitHeaderText}>Revenue Splits</Text>
              </View>
              <Ionicons
                name={showSplitBreakdown ? 'chevron-up' : 'chevron-down'}
                size={20}
                color="#8D8E90"
              />
            </TouchableOpacity>

            {showSplitBreakdown && (
              <View style={styles.splitBreakdown}>
                <View style={styles.splitRow}>
                  <Text style={styles.splitLabel}>Gross Earnings:</Text>
                  <Text style={styles.splitValue}>
                    ${(props.chargerHistory.grossAmount ?? 0).toFixed(2)}
                  </Text>
                </View>

                <View style={styles.splitDivider} />

                <Text style={styles.splitSubheader}>Split Details:</Text>
                {props.chargerHistory.splits?.map((split, index) => (
                  <View key={split.splitId || index} style={styles.partnerRow}>
                    <View style={styles.partnerInfo}>
                      <Text style={styles.partnerName}>{split.partnerUsername}</Text>
                      {split.partnerEmail && (
                        <Text style={styles.partnerEmail}>{split.partnerEmail}</Text>
                      )}
                    </View>
                    <View style={styles.partnerAmounts}>
                      <Text style={styles.partnerPercentage}>
                        {split.splitPercentage}%
                      </Text>
                      <Text style={styles.partnerAmount}>
                        -${split.splitAmount.toFixed(2)}
                      </Text>
                    </View>
                  </View>
                ))}

                <View style={styles.splitDivider} />

                <View style={[styles.splitRow, styles.netRow]}>
                  <Text style={styles.netLabel}>Net to You:</Text>
                  <Text style={styles.netValue}>
                    ${(props.chargerHistory.netAmount ?? 0).toFixed(2)}
                  </Text>
                </View>
              </View>
            )}
          </View>
        )}
      </View>
    </React.Fragment>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#0B1E1D',
  },
  containerBgColor: {
    backgroundColor: '#1A1D21',
  },
  searchIcon: {
    backgroundColor: '#39BEB7',
    borderRadius: 50,
    paddingVertical: 12,
    paddingHorizontal: 13,
    marginRight: 8,
  },
  inputContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'white',
    borderRadius: 25,
    paddingHorizontal: 20,
  },
  input: {
    flex: 1,
    paddingVertical: 10,
    //outlineStyle: 'none',
  },
  filterIcon: {
    height: 19,
    width: 19,
    position: 'absolute',
    right: 10,
    top: -9,
  },
  BgColor: {
    backgroundColor: '#51B868',
  },
  BgColor1: {
    backgroundColor: '#F97777',
  },
  textColor: {
    color: '#8D8E90',
  },
  textColor1: {
    color: '#E8E8E9',
  },
  textColor2: {
    color: '#39BEB7',
  },
  alignEllipses: Platform.select({
    web: {
      fontWeight: '500',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      maxWidth: 150,
    },
    default: {
      fontWeight: '500',
      overflow: 'hidden',
      maxWidth: 150,
    },
  }),
  centeredView: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 22,
  },
  modalView: {
    margin: 20,
    backgroundColor: '#292D33',
    borderRadius: 30,
    padding: 10,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
    width: 290,
    height: 250,
  },
  dropdownTrigger: {
    width: width <= 430 ? width * 0.4 : 150,
  },
  dropdownContainer: {
    position: 'absolute',
    top: 30,
    left: 0,
    backgroundColor: '#fff',
    borderRadius: 5,
    marginTop: 2,
    width: width <= 430 ? width * 0.4 : 150,
    zIndex: 1000,
  },
  line: {
    borderBottomColor: '#636363',
    borderBottomWidth: 1,
    borderStyle: 'dashed',
    marginVertical: 20,
  },
  BgColor3: {
    backgroundColor: '#39BEB7',
  },
  BgColor4: {
    backgroundColor: '#636363',
  },
  backSpace: {
    backgroundColor: '#39BEB7',
    borderRadius: 50,
    paddingVertical: 13,
    paddingHorizontal: 15,
    marginRight: 8,
    marginLeft: 8,
    marginTop: 7,
  },
  alignCrossIcon: {
    marginLeft: 5,
    marginTop: 2,
  },
  chargerNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  splitIndicator: {
    backgroundColor: 'rgba(59, 130, 246, 0.2)',
    borderRadius: 10,
    padding: 3,
  },
  splitSection: {
    borderTopWidth: 1,
    borderTopColor: '#1A1D23',
    paddingTop: 12,
    paddingBottom: 12,
    paddingHorizontal: 12,
  },
  splitHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
  },
  splitHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  splitHeaderText: {
    color: '#3B82F6',
    fontSize: 14,
    fontWeight: '600',
  },
  splitBreakdown: {
    backgroundColor: '#1A1D23',
    borderRadius: 12,
    padding: 12,
    marginTop: 8,
  },
  splitRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 6,
  },
  splitLabel: {
    color: '#E8E8E9',
    fontSize: 14,
    fontWeight: '400',
  },
  splitValue: {
    color: '#E8E8E9',
    fontSize: 14,
    fontWeight: '600',
  },
  splitDivider: {
    height: 1,
    backgroundColor: '#292D33',
    marginVertical: 8,
  },
  splitSubheader: {
    color: '#8D8E90',
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
    marginBottom: 8,
    marginTop: 4,
  },
  partnerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    backgroundColor: '#292D33',
    paddingHorizontal: 10,
    borderRadius: 8,
    marginBottom: 6,
  },
  partnerInfo: {
    flex: 1,
  },
  partnerName: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  partnerEmail: {
    color: '#8D8E90',
    fontSize: 11,
    marginTop: 2,
  },
  partnerAmounts: {
    alignItems: 'flex-end',
  },
  partnerPercentage: {
    color: '#3B82F6',
    fontSize: 12,
    fontWeight: '600',
  },
  partnerAmount: {
    color: '#F59E0B',
    fontSize: 14,
    fontWeight: '700',
    marginTop: 2,
  },
  netRow: {
    paddingTop: 8,
    marginTop: 4,
  },
  netLabel: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '700',
  },
  netValue: {
    color: '#51B868',
    fontSize: 16,
    fontWeight: '700',
  },
  // New styles for revenue split visualization
  revenueHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 2,
  },
  splitIndicatorBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 3,
    gap: 3,
  },
  splitDeductionText: {
    color: '#F59E0B',
    fontSize: 11,
    fontWeight: '600',
  },
  netRevenueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 2,
  },
  netRevenueLabel: {
    color: '#8D8E90',
    fontSize: 11,
    fontWeight: '500',
  },
  netRevenueValue: {
    color: '#51B868',
    fontSize: 12,
    fontWeight: '700',
  },
  expandedSplitSection: {
    backgroundColor: '#1A1D23',
    marginHorizontal: 12,
    marginBottom: 12,
    borderRadius: 10,
    padding: 12,
    borderWidth: 1,
    borderColor: '#3B82F6',
  },
  splitDetailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 4,
  },
  splitDetailLabel: {
    color: '#E8E8E9',
    fontSize: 13,
    fontWeight: '500',
  },
  splitDetailValue: {
    color: '#E8E8E9',
    fontSize: 13,
    fontWeight: '600',
  },
  splitDetailLabelDeduction: {
    color: '#E8E8E9',
    fontSize: 13,
    fontWeight: '500',
  },
  splitDetailValueDeduction: {
    color: '#F59E0B',
    fontSize: 13,
    fontWeight: '700',
  },
  splitDividerThin: {
    height: 1,
    backgroundColor: '#292D33',
    marginVertical: 6,
  },
  netRevenueExpandedLabel: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
  },
  netRevenueExpandedValue: {
    color: '#51B868',
    fontSize: 15,
    fontWeight: '700',
  },
});

export default ChargeHistoryCard;
